/**
 * Condensed budget card for a homepage column: two spend KPIs, one row per
 * enforcement level (key → personal → team) and a month-to-date spend strip.
 * Rows share fixed tracks — ring, `minmax(0, 1fr)` name, right-aligned amount —
 * so figures line up and a long key alias ellipsizes instead of widening the
 * card. Every level always renders (an empty ring when uncapped) for a stable shape.
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Button, ButtonLink, Link, Skeleton, Text } from '@backstage/ui';
import { RiArrowDownSLine } from '@remixicon/react';
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  ReferenceLine,
  XAxis,
} from 'recharts';
import { useApi } from '@backstage/core-plugin-api';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import { useLiteLLMProfile } from '../hooks/useLiteLLMProfile';
import { liteLlmApiRef } from '../api';
import { rootRouteRef } from '../routes';
import { UserInfo, TeamInfo, VirtualKey } from '../types';
import { fmtUsd } from '../format';
import { monthToDateRange, toLocalDay } from '../dates';
import {
  resolveCtas,
  type BudgetCta,
  type BudgetCtaKind,
  type BudgetCtaSpec,
} from '../ctas';
import {
  widgetViewState,
  USAGE_UNAVAILABLE_MSG,
  UNPROVISIONED_MSG,
} from '../widgetState';
import {
  allBudgetLimits,
  buildBudgetGauges,
  buildBudgetSummary,
  budgetHeadline,
  BudgetGauge,
  BudgetLimit,
  budgetTone,
  fmtBudgetDuration,
} from '../budget';
import {
  mtdCaption,
  sparklineAriaLabel,
  spendOnDay,
} from '../homeWidgetHelpers';
import { ChartTooltip, SERIES, StatusPill, type Tone } from './ui';
import { Gauge } from './Gauge';
import { GenerateKeyButton } from './GenerateKeyButton';
import { BudgetLimitList, LimitListPanel } from './BudgetLimitList';
import styles from './LiteLLMBudgetGauges.module.css';

export type { BudgetCtaKind, BudgetCtaSpec, BudgetCta } from '../ctas';

export interface LiteLLMBudgetGaugesProps {
  /** Card title. Defaults to 'Budget'. */
  title?: string;
  /**
   * Draw the card and title. Off inside a host that already supplies them,
   * such as the home grid. Defaults to true.
   */
  frame?: boolean;
  /** Ring diameter in px; rings shrink to 48 on narrow screens. Defaults to 56. */
  size?: number;
  /** Where the "+N more" key link points. Defaults to the Keys tab. */
  keysHref?: string;
  /** Where the module CTA points. Defaults to the LiteLLM page. */
  moduleHref?: string;
  /** Handles "Generate New Key"; otherwise it deep-links with `?generate=1`. */
  onCreateKey?: () => void;
  /**
   * Action-bar buttons, in order. Defaults to `['module', 'all-limits']`, with
   * 'new-key' added for users with no keys; `[]` hides the bar.
   */
  ctas?: BudgetCta[];
  /** Max key limits listed in the expanded view. Defaults to 8. */
  maxExpandedKeys?: number;
  /** Controlled expanded state for the all-limits view. */
  expanded?: boolean;
  /** Initial expanded state when uncontrolled. Defaults to false. */
  defaultExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** Fully custom node pinned after the CTAs, below a divider. */
  action?: ReactNode;
  /** Preloaded data; fetched through the profile hook when omitted. */
  userInfo?: UserInfo | null;
  teams?: TeamInfo[];
  keys?: VirtualKey[];
}

/** One-word level names, and the note shown when the level has no cap. */
const LEVEL: Record<BudgetGauge['kind'], { name: string; none: string }> = {
  key: { name: 'Key', none: 'No cap (unlimited)' },
  user: { name: 'User', none: 'No personal budget' },
  team: { name: 'Team', none: 'No team budget' },
};

const CTA_LABELS: Record<BudgetCtaKind, string> = {
  'new-key': 'Generate New Key',
  module: 'Open LiteLLM',
  'all-limits': 'All limits',
};

/** Compact USD for the tight budget caption — drops trailing cents. */
function fmtUsdShort(n: number): string {
  const v = n ?? 0;
  if (v >= 100 && Number.isInteger(v)) return `$${v}`;
  return fmtUsd(v);
}

/** Right-track amount: "$213 / $240", or just the percentage when hidden. */
function amountCaption(limit?: BudgetLimit): string {
  if (!limit) return '';
  if (limit.hidden) return `${Math.round(limit.pct)}%`;
  return `${fmtUsdShort(limit.spend)} / ${fmtUsdShort(limit.budget)}`;
}

/** Second right-track line: the reset window, or why dollars are missing. */
function amountNote(limit?: BudgetLimit): string {
  if (!limit) return '';
  if (limit.hidden) return 'hidden by admin';
  const window = fmtBudgetDuration(limit.budgetDuration);
  return window ? `resets ${window}` : 'never resets';
}

/** Uppercase micro-label for levels, KPIs and the chart caption. */
const Tag = ({ children, tone }: { children: ReactNode; tone?: Tone }) => (
  <Text
    variant="body-x-small"
    className={styles.tag}
    data-tone={tone}
    data-toned={!!tone}
  >
    {children}
  </Text>
);

/** One inline KPI: label over a tabular value over a fixed-height hint. */
const InlineStat = ({
  label,
  value,
  hint,
  title,
}: {
  label: string;
  value: string;
  hint?: string;
  title?: string;
}) => (
  <div className={styles.stat}>
    <div className={styles.statLabel}>
      <span className={styles.statDot} data-tone="accent" />
      <Tag>{label}</Tag>
    </div>
    <Text variant="body-large" className={styles.statValue} title={title}>
      {value}
    </Text>
    <Text variant="body-x-small" className={styles.statHint}>
      {hint ?? ' '}
    </Text>
  </div>
);

/**
 * One level as a row. The middle track's third line goes to "Over cap" first,
 * then the "+N more" link, then the identifying detail (key id, email); the
 * reset window lives only in the right track so it never prints twice.
 */
const LevelRow = ({
  gauge,
  size,
  keysHref,
}: {
  gauge: BudgetGauge;
  size: number;
  keysHref?: string;
}) => {
  const { name, none } = LEVEL[gauge.kind];
  const limit = gauge.limit;
  const tone: Tone = limit ? budgetTone(limit.pct) : 'neutral';
  const extra = gauge.kind === 'key' ? Math.max(0, gauge.count - 1) : 0;
  const overCap = Boolean(limit && limit.pct > 100);

  const subNote = (): ReactNode => {
    if (overCap) return 'Over cap';
    if (extra > 0 && keysHref) {
      return (
        <Link href={keysHref} variant="body-x-small" className={styles.more}>
          +{extra} more
        </Link>
      );
    }
    return limit?.sublabel ?? null;
  };

  return (
    <div className={styles.row}>
      <Gauge
        value={limit?.pct ?? 0}
        tone={tone}
        size={size}
        label={limit ? `${Math.round(limit.pct)}%` : '—'}
        ariaLabel={
          limit
            ? `${name} budget used: ${Math.round(limit.pct)}%`
            : `${name} — no cap`
        }
      />

      <div className={styles.middle}>
        {/* Only a warning or over-cap limit earns a coloured level tag. */}
        <Tag tone={tone === 'warning' || tone === 'danger' ? tone : undefined}>
          {name}
        </Tag>
        {limit ? (
          <>
            <Text
              variant="body-x-small"
              className={styles.limitName}
              title={
                limit.sublabel
                  ? `${limit.label} · ${limit.sublabel}`
                  : limit.label
              }
            >
              {limit.label}
            </Text>
            <Text
              variant="body-x-small"
              className={styles.subNote}
              data-over={overCap}
            >
              {subNote()}
            </Text>
          </>
        ) : (
          <Text variant="body-x-small" className={styles.subNote}>
            {none}
          </Text>
        )}
      </div>

      <div className={styles.amount}>
        <Text
          variant="body-small"
          className={styles.amountValue}
          data-empty={!limit}
          title={
            limit
              ? `${fmtUsd(limit.spend)} of ${fmtUsd(limit.budget)}`
              : undefined
          }
        >
          {amountCaption(limit)}
        </Text>
        <Text variant="body-x-small" className={styles.subNote}>
          {amountNote(limit)}
        </Text>
      </div>
    </div>
  );
};

interface DailySpend {
  date: string;
  spend: number;
}

/** Full-width MTD spend sparkline, with the total on its caption line. */
const SpendStrip = ({
  data,
  mtdSpend,
  loading,
  usageUnavailable,
  maxBudget,
}: {
  data: DailySpend[];
  mtdSpend: number;
  loading: boolean;
  usageUnavailable: boolean;
  maxBudget?: number;
}) => {
  const renderPlot = () => {
    if (loading) return <Skeleton width="100%" height="100%" />;
    if (data.length === 0) return <div className={styles.plotEmpty}>—</div>;
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={data}
          margin={{ top: 4, right: 0, bottom: 0, left: 0 }}
        >
          <XAxis dataKey="date" hide />
          <Tooltip content={<ChartTooltip valueFormatter={fmtUsd} />} />
          {maxBudget !== undefined && (
            <ReferenceLine
              y={maxBudget}
              stroke={SERIES.budget}
              strokeDasharray="4 4"
              strokeWidth={1}
            />
          )}
          <Area
            type="monotone"
            dataKey="spend"
            name="Spend"
            stroke={SERIES.spend}
            strokeWidth={1.5}
            fill={SERIES.spend}
            fillOpacity={0.25}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  let caption = mtdCaption();
  if (loading) caption = '…';
  else if (usageUnavailable) caption = USAGE_UNAVAILABLE_MSG;
  else if (data.length === 0) caption = 'No spend this month';
  const quiet = loading || usageUnavailable;

  return (
    <div
      role="img"
      aria-label={sparklineAriaLabel(data)}
      className={styles.strip}
    >
      <div className={styles.plot}>{renderPlot()}</div>
      <div className={styles.stripCaption}>
        <div className={styles.stripLabel}>
          <Tag>Spent (mtd)</Tag>
          <Text variant="body-x-small" className={styles.subNote}>
            {caption}
          </Text>
        </div>
        <Text
          variant="body-small"
          className={styles.amountValue}
          title={quiet ? undefined : `${fmtUsd(mtdSpend)} spent month to date`}
        >
          {quiet ? '—' : fmtUsd(mtdSpend)}
        </Text>
      </div>
    </div>
  );
};

/** KPI value while loading / unavailable: '…' → '—' → the figure. */
function statValue(loading: boolean, unavailable: boolean, value: string) {
  if (loading) return '…';
  if (unavailable) return '—';
  return value;
}

/** KPI hint: nothing while loading, the reason when unavailable. */
function statHint(loading: boolean, unavailable: boolean, hint?: string) {
  if (loading) return undefined;
  if (unavailable) return USAGE_UNAVAILABLE_MSG;
  return hint;
}

export const LiteLLMBudgetGauges = ({
  title = 'Budget',
  frame = true,
  size = 56,
  keysHref,
  moduleHref: propModuleHref,
  onCreateKey,
  ctas,
  maxExpandedKeys = 8,
  expanded,
  defaultExpanded = false,
  onExpandedChange,
  action,
  userInfo: propUserInfo,
  teams: propTeams,
  keys: propKeys,
}: LiteLLMBudgetGaugesProps) => {
  const api = useApi(liteLlmApiRef);
  const expandedRegionId = useId();
  const routeLink = useRouteRef(rootRouteRef);
  const moduleHref = propModuleHref ?? routeLink?.();
  const profile = useLiteLLMProfile();
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [dailySpend, setDailySpend] = useState<DailySpend[]>([]);
  const [mtdSpend, setMtdSpend] = useState(0);

  const preloaded = propUserInfo !== undefined;
  const user = preloaded ? propUserInfo : profile.userInfo;
  const teams = propTeams ?? profile.teams;
  const keys = propKeys ?? profile.keys;

  const isControlled = expanded !== undefined;
  const [uncontrolledExpanded, setUncontrolledExpanded] =
    useState(defaultExpanded);
  const isExpanded = expanded ?? uncontrolledExpanded;

  const toggleExpanded = useCallback(() => {
    const next = !isExpanded;
    if (!isControlled) setUncontrolledExpanded(next);
    onExpandedChange?.(next);
  }, [isControlled, isExpanded, onExpandedChange]);

  const keysTabHref =
    keysHref ?? (moduleHref ? `${moduleHref}?tab=keys` : undefined);

  useEffect(() => {
    let cancelled = false;
    setUsageLoading(true);
    setUsageError(null);
    // Frozen to month-to-date; usage degrades independently of the gauges.
    const { startDate, endDate } = monthToDateRange();
    api
      .getUsage(startDate, endDate)
      .then(result => {
        if (cancelled) return;
        setDailySpend(
          (result.daily_usage ?? []).map(d => ({
            date: d.date,
            spend: d.spend ?? 0,
          })),
        );
        setMtdSpend(result.total_spend ?? 0);
        setUsageLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setUsageError(err?.message ?? 'Failed to load usage data');
        setDailySpend([]);
        setMtdSpend(0);
        setUsageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Every budgeted key, so the nearest-per-level gauge is accurate.
  const summary = useMemo(
    () => buildBudgetSummary(user, teams, keys, keys.length),
    [user, teams, keys],
  );
  const gauges = useMemo(() => buildBudgetGauges(summary), [summary]);
  const headline = useMemo(() => budgetHeadline(summary), [summary]);

  const expandedSummary = useMemo(
    () => buildBudgetSummary(user, teams, keys, maxExpandedKeys),
    [user, teams, keys, maxExpandedKeys],
  );
  const expandedLimits = useMemo(
    () => allBudgetLimits(expandedSummary),
    [expandedSummary],
  );

  const viewState = useMemo(
    () =>
      widgetViewState({
        loading: preloaded ? false : profile.loading,
        error: preloaded ? null : profile.error,
        userInfo: user,
        usageError,
        hasKeys: keys.length > 0,
      }),
    [preloaded, profile.loading, profile.error, user, usageError, keys],
  );
  const usageUnavailable =
    viewState.kind === 'ready' && viewState.usageUnavailable;

  // Today and yesterday come from the MTD series — no extra request.
  const today = new Date();
  const todaySpend = spendOnDay(dailySpend, today);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  // No point for yesterday when the month started today: say nothing.
  const yesterdaySpend = dailySpend.some(d => d.date === toLocalDay(yesterday))
    ? spendOnDay(dailySpend, yesterday)
    : null;
  const todayHint =
    yesterdaySpend === null
      ? undefined
      : `vs ${fmtUsd(yesterdaySpend)} yesterday`;

  const summaryText =
    headline.count === 0
      ? 'no limits apply'
      : `${headline.count} limit${headline.count === 1 ? '' : 's'}`;

  const resolvedCtas = useMemo(
    () =>
      resolveCtas({
        ctas,
        hasKeys: keys.length > 0,
        viewState,
        limitCount: headline.count,
      }),
    [ctas, keys.length, viewState, headline.count],
  );

  const renderCta = (
    cta: BudgetCtaSpec & { kind: BudgetCtaKind },
    index: number,
  ) => {
    const label = cta.label ?? CTA_LABELS[cta.kind];
    const key = `${cta.kind}-${index}`;
    switch (cta.kind) {
      case 'new-key':
        if (onCreateKey) {
          return (
            <GenerateKeyButton
              key={key}
              size="small"
              label={label}
              onClick={onCreateKey}
            />
          );
        }
        if (!moduleHref) return null;
        return (
          <GenerateKeyButton
            key={key}
            size="small"
            label={label}
            to={`${moduleHref}?generate=1`}
          />
        );
      case 'module':
        if (!moduleHref) return null;
        return (
          <ButtonLink
            key={key}
            size="small"
            variant="tertiary"
            href={moduleHref}
          >
            {label}
          </ButtonLink>
        );
      case 'all-limits':
        return (
          <Button
            key={key}
            size="small"
            variant="tertiary"
            onPress={toggleExpanded}
            aria-expanded={isExpanded}
            aria-controls={expandedRegionId}
            iconEnd={
              <RiArrowDownSLine
                className={styles.chevron}
                data-expanded={isExpanded}
              />
            }
          >
            {label}
          </Button>
        );
      default:
        return null;
    }
  };

  const hasFooter = resolvedCtas.length > 0 || !!action;

  const renderBody = () => {
    switch (viewState.kind) {
      case 'loading':
        return (
          <div className={styles.skeletons}>
            {[0, 1, 2].map(i => (
              <div key={i} className={styles.skeletonRow}>
                <Skeleton width={size} height={size} rounded />
                <div className={styles.skeletonLines}>
                  <Skeleton width="70%" height={12} />
                  <Skeleton width="45%" height={11} />
                </div>
                <Skeleton width={64} height={12} />
              </div>
            ))}
          </div>
        );
      // Quiet, generic copy: raw backend errors never reach a widget.
      case 'error':
        return (
          <Text variant="body-small" className={styles.quiet}>
            {viewState.message}
          </Text>
        );
      case 'unprovisioned':
        return (
          <Text variant="body-small" className={styles.quiet}>
            {UNPROVISIONED_MSG}
          </Text>
        );
      default:
        return (
          <>
            <div className={styles.stats}>
              <InlineStat
                label="Today spent"
                value={statValue(
                  usageLoading,
                  usageUnavailable,
                  fmtUsd(todaySpend),
                )}
                hint={statHint(usageLoading, usageUnavailable, todayHint)}
                title={
                  usageLoading || usageUnavailable
                    ? undefined
                    : 'Spend since local midnight'
                }
              />
              <span className={styles.statDivider} />
              <InlineStat
                label="Month to date"
                value={statValue(
                  usageLoading,
                  usageUnavailable,
                  fmtUsd(mtdSpend),
                )}
                hint={statHint(usageLoading, usageUnavailable, mtdCaption())}
                title={
                  usageLoading || usageUnavailable
                    ? undefined
                    : 'Spend from the 1st of this month'
                }
              />
            </div>

            <div className={styles.rows}>
              {gauges.map(gauge => (
                <LevelRow
                  key={gauge.kind}
                  gauge={gauge}
                  size={size}
                  keysHref={keysTabHref}
                />
              ))}
            </div>

            <SpendStrip
              data={dailySpend}
              mtdSpend={mtdSpend}
              loading={usageLoading}
              usageUnavailable={usageUnavailable}
              maxBudget={user?.max_budget ?? undefined}
            />

            {isExpanded && (
              <div id={expandedRegionId} className={styles.expanded}>
                <LimitListPanel>
                  <BudgetLimitList
                    limits={expandedLimits}
                    hiddenKeyCount={expandedSummary.hiddenBudgetedKeys}
                    keysHref={keysTabHref}
                  />
                </LimitListPanel>
              </div>
            )}
          </>
        );
    }
  };

  const body = (
    <>
      <div className={styles.head} data-frame={frame}>
        {frame && (
          <Text variant="title-x-small" className={styles.title}>
            {title}
          </Text>
        )}
        {viewState.kind === 'ready' && (
          <StatusPill
            label={summaryText}
            tone={budgetTone(headline.closest?.pct ?? 0)}
            dot={headline.count > 0}
          />
        )}
      </div>
      {renderBody()}
      {hasFooter && (
        <div className={styles.footer}>
          {resolvedCtas.map(renderCta)}
          {action}
        </div>
      )}
    </>
  );

  // Rings shrink on narrow screens via --gauge-size, see the stylesheet.
  const sizeVars = {
    '--gauge-ring': `${Math.min(48, size)}px`,
    '--strip-height': `${size}px`,
  } as CSSProperties;

  return (
    <div className={frame ? styles.card : styles.bare} style={sizeVars}>
      {body}
    </div>
  );
};
